import { defineStore, storeToRefs } from "pinia";
import {
  TrainingPlanDetailsItemTypeEnum,
  BookingsApi,
  type CancelBookingDto,
  type CreateBookingsDto,
  SessionDetailsApi,
  RemoveBookingDtoRequestActionEnum,
  type ItemType as ItemTypeSDK,
} from "@simbelapp/order-sdk";
import { useSessionDetailsStore } from "./session-details-store";
import type { ISessionsDetailsAttendeesStore } from "~/utils/interfaces/sessions-interfaces";

import { useFeedback } from "~/composables/feedback/useFeedback";
import { AddAttendeesStep } from "~/utils/enums/addAttendees.enums";
import { ItemType, MicroserviceEnum } from "~/utils/enums/common.enums";
import { FAKE_SESSION_ID } from "~/utils/enums/session-details-enums";
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { usePagination } from "~/composables/paginations/usePagination";
import type { PaginationKeysEnum } from "~/utils/enums/pagination-keys.enums";
import { useUsersStore } from "~/store/users/users.store";
import { exceedsCapacity } from "~/utils/helpers/sessions/session-capacity";

export const useSessionDetailsAttendeesStore = defineStore("sessionDetailsAttendeesStore", {
  state: (): ISessionsDetailsAttendeesStore => {
    return {
      sessionDetailsAttendees: null,
      sessionAttendeesMultiSelect: {
        selectedItems: [],
        excludedItems: [],
      },
      sessionAttendeesSelectAll: false,
      sessionAddAttendeesMultiSelect: {
        selectedItems: [],
        excludedItems: [],
      },
      // unavailable users
      sessionUnavailableUsers: null,
      sessionUnavailableUsersSelectAll: false,
      sessionUnavailableUsersMultiSelect: {
        selectedItems: [],
        excludedItems: [],
      },
      sessionAddAttendeesSelectAll: false,
      sessionDetailsUnscheduleUsers: null,
      showAddAttendeeModal: false,
      showDeleteAttendeesModal: false,
      showUnscheduleAttendeesModal: false,
      updateUnscheduleAttendeeModal: false,
      showExceedNotificationsModal: false,
      deliveryChoiceAction: null,
      showActionsFooter: false,
      actionExceededDirectNotify: false,
      removeAttendee: {
        showDeleteModal: false,
        showRequestModal: false,
        showNotifyModal: false,
        bookingId: null,
        isLinkedToARequest: false,
        doNotNotify: false,
        requestAction: null,
        requestComment: null,
      },
      addAttendeesStep: AddAttendeesStep.ADD_ATTENDEES_TABLE,
    };
  },
  getters: {
    /** « Tout sélectionner » leaves `selectedItems` empty: the count is then the table's total minus its exclusions. */
    addAttendeesSelectedCount(): number {
      if (this.sessionAddAttendeesSelectAll) {
        const total = Number(useUsersStore().companyUsersFullDatas?.meta?.count ?? 0);
        return total - (this.sessionAddAttendeesMultiSelect.excludedItems?.length ?? 0);
      }
      return this.sessionAddAttendeesMultiSelect.selectedItems?.length ?? 0;
    },
    addAttendeesExceedsCapacity(): boolean {
      const details = useSessionDetailsStore().sessionDetails;
      return exceedsCapacity(details?.nbAttendees, this.addAttendeesSelectedCount, details?.nbMaxAttendees);
    },
  },
  actions: {
    closeRemoveAttendeeFlow() {
      this.removeAttendee = {
        showDeleteModal: false,
        showRequestModal: false,
        showNotifyModal: false,
        bookingId: null,
        isLinkedToARequest: false,
        doNotNotify: false,
        requestAction: null,
        requestComment: null,
      };
    },
    resetIndexGrid() {
      if (this.sessionAttendeesMultiSelect) {
        this.sessionAttendeesMultiSelect.selectedItems = [];
        this.sessionAttendeesMultiSelect.excludedItems = [];
        this.sessionAttendeesSelectAll = false;
      }
    },
    resetSessionAddAttendeesMultiSelect() {
      if (this.sessionAddAttendeesMultiSelect) {
        this.sessionAddAttendeesMultiSelect.selectedItems = [];
        this.sessionAddAttendeesMultiSelect.excludedItems = [];
        this.sessionAddAttendeesSelectAll = false;
      }
    },
    resetSessionUnavailableUsers() {
      if (this.sessionUnavailableUsersMultiSelect) {
        this.sessionUnavailableUsersMultiSelect.selectedItems = [];
        this.sessionUnavailableUsersMultiSelect.excludedItems = [];
        this.sessionUnavailableUsersSelectAll = false;
        this.sessionUnavailableUsers = null;
      }
    },
    async fetchSessionAttendeesFullDatas(
      itemId: string,
      itemType: ItemType | TrainingPlanDetailsItemTypeEnum,
      paginationOptions?: Record<string, string>,
    ) {
      let transformQuery = {};
      const feedback = useFeedback();
      if (paginationOptions) {
        transformQuery = Object.fromEntries(new URLSearchParams(paginationOptions).entries());
      } else {
        transformQuery = {
          noLimit: true,
        };
      }

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
      try {
        const data = await apiInstance.getTrainingPlanAttendeesFull({
          itemId,
          itemType,
          ...transformQuery,
        });
        if (data) {
          this.sessionDetailsAttendees = {
            data: data?.data?.filter((a) => a.sessionId !== FAKE_SESSION_ID),
            meta: {
              count: data.meta.count,
              totalCount: data.meta.totalCount,
            },
          };
        }
      } catch {
        feedback.error(`Une erreur est survenue lors de la récupération des participants`, "small");
      }
    },

    async fetchSessionUnscheduleUsers(
      itemId: string,
      itemType: ItemType | TrainingPlanDetailsItemTypeEnum,
      paginationOptions?: Record<string, string>,
    ) {
      if (itemType === ItemType.PROJECT) {
        this.sessionDetailsUnscheduleUsers = null;
        return;
      }
      const feedback = useFeedback();
      let transformQuery = {};

      if (paginationOptions) {
        transformQuery = Object.fromEntries(new URLSearchParams(paginationOptions).entries());
      } else {
        // Fallback for existing calls
        const pagination = usePagination();
        const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
        transformQuery = Object.fromEntries(new URLSearchParams(paginateOptions).entries());
      }

      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
      try {
        const data = await apiInstance.getTrainingPlanAttendeesFull({
          itemId,
          itemType,
          ...transformQuery,
          isCanceled: true,
        });
        if (data) {
          this.sessionDetailsUnscheduleUsers = {
            data: data?.data?.filter((a) => a.sessionId !== FAKE_SESSION_ID),
            meta: {
              count: data.meta.count,
              totalCount: data.meta.totalCount,
            },
          };
        }
      } catch {
        feedback.error(`Une erreur est survenue lors de la récupération des participants`, "small");
      }
    },

    async deleteAttendeesFromSession(
      sessionId: string,
      itemType: TrainingPlanDetailsItemTypeEnum,
      bookingIds: string[],
      doNotNotify: boolean,
      paginationKey?: PaginationKeysEnum,
    ) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
      try {
        await apiInstance.batchRemoveBookings({
          batchRemoveBookingsDto: {
            bookingIds,
            removeBookingDto: {
              doNotNotify,
            },
          },
        });
        feedback.success(`Vos modifications ont été sauvegardées`, "small");
        if (paginationKey) {
          const pagination = usePagination(paginationKey);
          const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
          await this.fetchSessionAttendeesFullDatas(sessionId, itemType, paginateOptions);
        } else {
          await this.fetchSessionAttendeesFullDatas(sessionId, itemType);
        }
      } catch {
        feedback.error(`Une erreur est survenue lors de la suppression`, "small");
      }
    },
    async removeAttendeeAction(
      bookingId: string,
      doNotNotify: boolean,
      isLinkedToARequest: boolean,
      requestAction?: RemoveBookingDtoRequestActionEnum | null,
      requestComment?: string | null,
    ) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
      try {
        await apiInstance.removeBooking({
          id: bookingId,
          removeBookingDto: {
            doNotNotify,
            ...(requestAction && { requestAction }),
            ...(requestComment && { requestComment }),
          },
        });
        feedback.success(`Vos modifications ont été sauvegardées`, "small");
      } catch {
        feedback.error(`Une erreur est survenue lors de la suppression`, "small");
      }
    },
    async executeRemoveAttendee() {
      const { bookingId, doNotNotify, isLinkedToARequest, requestAction, requestComment } = this.removeAttendee;
      if (!bookingId) return;
      await this.removeAttendeeAction(bookingId, doNotNotify, isLinkedToARequest, requestAction, requestComment);
    },

    async unscheduleAttendeesFromSession(
      sessionId: string,
      itemType: TrainingPlanDetailsItemTypeEnum,
      bookingIds: string[],
      cancelBookingDto: CancelBookingDto,
      paginationAttendeesKey?: PaginationKeysEnum,
      paginationUnscheduleKey?: PaginationKeysEnum,
      requestAction?: RemoveBookingDtoRequestActionEnum | null,
      requestComment?: string | null,
    ) {
      const feedback = useFeedback();
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
      try {
        await apiInstance.batchCancelBookings({
          batchCancelBookingsDto: {
            bookingIds,
            cancelBookingDto,
            ...(requestAction && { requestAction }),
            ...(requestComment && { requestComment }),
          },
        });
        feedback.success(`Vos modifications ont été sauvegardées`, "small");

        const paginationAttendees = usePagination(paginationAttendeesKey);
        const paginateOptions = paginationAttendees.getFindOptions(paginationAttendees.paginateOptions.value);
        await this.fetchSessionAttendeesFullDatas(sessionId, itemType, paginateOptions);

        const paginationUnschedule = usePagination(paginationUnscheduleKey);
        const paginateOptionsUnschedule = paginationUnschedule.getFindOptions(
          paginationUnschedule.paginateOptions.value,
        );
        await this.fetchSessionUnscheduleUsers(sessionId, itemType, paginateOptionsUnschedule);
      } catch {
        feedback.error(`Une erreur est survenue lors de la déplanification`, "small");
      }
    },

    async updateUnscheduleAttendee(
      bookingId: string,
      cancelBookingDto: CancelBookingDto,
      paginationUnscheduleKey?: PaginationKeysEnum,
    ) {
      const feedback = useFeedback();
      const sessionDetailsStore = useSessionDetailsStore();
      const { sessionDetails } = storeToRefs(sessionDetailsStore);
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
      try {
        await apiInstance.editCancelBooking({
          id: bookingId,
          editCancelBookingDto: cancelBookingDto,
        });
        const sessionStore = useSessionsStore();
        await sessionStore.fetchTrainingPlanSession("tr-" + bookingId);
        if (sessionDetails.value?.id && sessionDetails.value?.itemType) {
          const pagination = usePagination(paginationUnscheduleKey);
          const paginateOptions = pagination.getFindOptions(pagination.paginateOptions.value);
          await this.fetchSessionUnscheduleUsers(
            sessionDetails.value?.id,
            sessionDetails.value?.itemType,
            paginateOptions,
          );
        }
        feedback.success(`Vos modifications ont été sauvegardées`, "small");
      } catch {
        feedback.error(`Une erreur est survenue lors de la modification`, "small");
      }
    },
    async createBookings(bookings: CreateBookingsDto) {
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
        const createBookings = await apiInstance.createBooking({
          createBookingsDto: bookings,
        });
        return { data: createBookings };
      } catch (error: any) {
        return { error };
      }
    },

    handleAddAttendeeModal(bool: boolean) {
      this.showAddAttendeeModal = bool;
      if (bool) {
        this.resetSessionAddAttendeesMultiSelect();
      }
    },

    async getSelectedAttendeesInSession(excludedUserIds: string[], itemId: string, itemType: ItemTypeSDK) {
      const feedback = useFeedback();
      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, SessionDetailsApi);
        const data = await apiInstance.getSelectedAttendeesInSession({
          itemId,
          itemType,
          getSelectedAttendeesDto: { excludedUserIds },
        });
        return data;
      } catch {
        feedback.error(`Une erreur est survenue lors de la récupération des participants`, "small");
        return null;
      }
    },
  },
});
